import { useSelector, useDispatch, useStore } from "react-redux";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { showToastWithTimeout } from "../../store/actions/message";
import { normalizeError, handleError } from "../../utils/errorHandling";
import axios from "../../axios";
import { chartTypeKeyMap } from "../../utils/constants/chartConstants";
import {
  getSelectorForCurrentPageByUuid,
  getSingleDataSourceFiltersQueryUuidByPage,
} from "../../utils/pages";
import { assembleEmptySingleQueryFiltersFromActiveTableStore } from "../../utils/menuFilters";
import { useShallowEqualSelector } from "../../store";
import { produce } from "immer";
import { getActiveTableAndViewUuidFromState } from "../../utils/activeTable";
import { convertToWithNull } from "../../store/actions/dataSettings/prepareSettings";
import { getQuery, resetQuery } from "../../store/actions";
import queryBuilder, {
  getVisibleSingleQueryFilterFields,
} from "../../store/actions/queryBuilder/queryBuilder";
import { HEADER_CONVERT_EMPTY_STRINGS_TO_NULL } from "../../utils/axios/common";
import { FieldNameWithAlias } from "../../utils/queries";
import { parse } from "qs";
import { isObject, isArray } from "lodash-es";
import { emptyObject } from "../../utils/constants/constants";

export default function (activeTab) {
  const dispatch = useDispatch();
  const getState = useStore().getState;

  const { isEnabledForThisPage } = activeTab ?? {};

  const visualization = activeTab?.blocks?.[0]?.visualizations?.[0];
  const visualizationSettings = visualization?.settings ?? emptyObject;
  const isActiveTable =
    visualizationSettings.type === chartTypeKeyMap.ActiveTable;
  const queryUuid = visualization?.query?.uuid;
  const query = useQuery(queryUuid, isActiveTable);

  const currentViewUuid = useSelector(
    (state) => state.activeTable.currentViewUuid
  );
  const { currentTable, currentActiveTableUuid } = useShallowEqualSelector(
    getActiveTableAndViewUuidFromState
  );
  const currentActiveTable = isActiveTable ? currentTable : null;
  const firstVisualizationActiveTableUuid =
    visualizationSettings.activeTableUuid;
  const currentViewFilters = currentActiveTable?.views?.find(
    (v) => v.uuid === currentViewUuid
  )?.filters;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const abortControllersRef = useRef(new Set());

  const getStateAdjusted = useCallback(
    () =>
      produce(getState(), (draft) => {
        // We don't want to take the single query filters into account,
        // otherwise the list of possible values will get stuck when filtering
        // any other field.
        draft.layout.singleQueryFiltersByQueryUuid = {};
      }),
    [getState]
  );
  const loadSingleFieldByName = useCallback(
    async (fieldName, alias, otherFieldValues, search, page) => {
      page ||= 1;
      const abortController = new AbortController();
      try {
        abortControllersRef.current.add(abortController);

        setData((data) =>
          produce(data, (draft) => {
            for (const row of draft) {
              if (row.fieldName !== fieldName) {
                continue;
              }

              row.loading = true;
            }
          })
        );

        const visualizationFilters = getVisualizationFilters(
          visualizationSettings,
          getStateAdjusted
        );

        const perPage = 100;
        const promise = axios.post(
          `/api/v1/queries/${queryUuid}/exec`,
          {
            page,
            perPage,
            format: "chart",
            order: {
              [fieldName]: "ASC",
            },
            overrides: {
              other: {
                isDistinct: true,
              },
              fields: [
                {
                  name: fieldName,
                  ds: alias,
                },
              ],
            },
            filters: convertToWithNull([
              ...otherFieldValues?.map(({ key, selectedValues }) => ({
                name: key,
                values: selectedValues,
              })),
              ...(currentViewFilters ?? []),
              ...(visualizationFilters ?? []),
              ...(search
                ? [{ name: fieldName, values: [search], operator: "like" }]
                : []),
            ]),
          },
          {
            signal: abortController.signal,
            headers: { [HEADER_CONVERT_EMPTY_STRINGS_TO_NULL]: "0" },
          }
        );

        const response = (await promise).data;

        setData((data) =>
          produce(data, (draft) => {
            for (const row of draft) {
              if (row.fieldName !== fieldName) {
                continue;
              }

              row.values = response.data.map((v) => v[fieldName]);
              row.totalCount = response.meta.pagination.total;
              if (!search) {
                row.originalTotalCount = response.meta.pagination.total;
              }
              row.perPage = perPage;
              row.loading = false;
            }
          })
        );
      } catch (e) {
        const normalizedError = handleError(e);
        setData((data) =>
          produce(data, (draft) => {
            for (const row of draft) {
              if (row.fieldName !== fieldName) {
                continue;
              }

              row.error = normalizedError;
              row.loading = false;
            }
          })
        );
      } finally {
        abortControllersRef.current.delete(abortController);
      }
    },
    [currentViewFilters, getStateAdjusted, queryUuid, visualizationSettings]
  );

  const fieldsWithAliases = useMemo(() => {
    return (
      (query &&
        getVisibleSingleQueryFilterFields(
          currentActiveTable?.views,
          currentActiveTable?.columns,
          currentViewUuid,
          query,
          visualization
        )) ??
      (query?.dataSources ?? []).flatMap((v) =>
        v.fields.map((field) => new FieldNameWithAlias(field.name, v.alias))
      )
    );
  }, [
    currentActiveTable?.columns,
    currentActiveTable?.views,
    currentViewUuid,
    query,
    visualization,
  ]);

  const isEnabledAndActiveTableReady = useMemo(() => {
    if (!isEnabledForThisPage) {
      return false;
    }

    return !(
      isActiveTable &&
      (!currentActiveTableUuid ||
        currentActiveTableUuid !== firstVisualizationActiveTableUuid)
    );
  }, [
    currentActiveTableUuid,
    firstVisualizationActiveTableUuid,
    isActiveTable,
    isEnabledForThisPage,
  ]);

  useEffect(() => {
    if (isEnabledAndActiveTableReady && !queryUuid) {
      const errorMessage = "Query was not found.";
      showToastWithTimeout(errorMessage, "danger");
      setError(
        normalizeError({
          message: errorMessage,
        })
      );
    }
  }, [dispatch, isEnabledAndActiveTableReady, queryUuid]);

  useEffect(() => {
    if (!query || !fieldsWithAliases) {
      return;
    }

    setData(
      assembleEmptySingleQueryFiltersFromActiveTableStore(
        query,
        fieldsWithAliases
      )
    );

    const abortControllers = abortControllersRef.current;
    return () => {
      for (const controller of abortControllers) {
        controller.abort();
      }
      setLoading(false);
      setData(null);
      setError(null);
    };
  }, [fieldsWithAliases, query]);

  useEffect(() => {
    return dispatch(resetQuery());
  }, [dispatch]);

  return {
    isEnabledForThisPage,
    loading,
    data,
    error,
    queryUuid,
    loadSingleFieldByName,
  };
}

/**
 * Returns the same filters array that the queryBuilder would produce with the
 * first chart on the page (typically for non-Active-Table).
 *
 * This way the single query filters can show fewer results (as desired),
 * only the ones relevant to the chart as per its filters.
 *
 * @param visualizationSettings {object}
 * @param getState {Function}
 * @returns {array|null}
 */
function getVisualizationFilters(visualizationSettings, getState) {
  try {
    const filters = parse(
      queryBuilder(visualizationSettings, getState)
    ).filters;
    // Ensure that it's an array.
    return isObject(filters) && !isArray(filters)
      ? Object.values(filters)
      : filters;
  } catch (e) {
    handleError(e, { showToast: false });
  }

  return null;
}

/**
 * Abstracts away data that are needed for both active tables and other charts,
 * they just need to be assembled in different ways.
 *
 * @param queryUuid
 * @param isActiveTable {boolean}
 * @returns ?Query
 */
function useQuery(queryUuid, isActiveTable) {
  const dispatch = useDispatch();

  const activeQuery = useSelector((state) => state.dataSettings.activeQuery);
  const activeTableQuery = useSelector((state) => state.activeTable.query);

  const query =
    isActiveTable && activeTableQuery ? activeTableQuery : activeQuery;

  useEffect(() => {
    if (!isActiveTable && queryUuid && activeQuery?.uuid !== queryUuid) {
      dispatch(getQuery(queryUuid));
    }
  }, [activeQuery?.uuid, dispatch, isActiveTable, queryUuid]);

  return query;
}

/**
 * Returns some basic data, the most important which being
 * whether single query filters are enabled for the page.
 */
export function useSingleQueryFiltersBasic(pageUuid) {
  const page = useSelector(getSelectorForCurrentPageByUuid(pageUuid));
  const queryUuid = getSingleDataSourceFiltersQueryUuidByPage(page);
  return {
    page,
    queryUuid,
    isSingleQueryFiltersEnabled: page?.singleDataSourceMenuFilters,
  };
}

export function useIsSingleQueryFiltersEnabledOnThisPage(pageUuid) {
  const page = useSelector(getSelectorForCurrentPageByUuid(pageUuid));
  return page?.singleDataSourceMenuFilters;
}
